import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Skeleton } from '@blade/ui-kit'
import { ProviderIcon } from '@root/entities/provider/ui'

import { useSwapParameters } from '../use-swap-parameters'

import { WalletSwapDetailsLine } from './wallet-swap-details-line'

import type { CurrencyModel } from '@root/entities/currency'
import type { PairModel } from '@root/entities/pair'
import type { Quote } from '@root/entities/quote'
import type { NotEnoughLiquidityError } from '@root/entities/quote/atoms'
import type { TradeSettings } from '@root/entities/trade-settings'
import type { Error } from '@root/shared'
import type { ApiError } from '@root/shared/api'
import type { ClassNameProps } from '@root/shared/react'

export type WalletSwapDetailsTableProps = {
  baseCurrency: CurrencyModel
  quoteCurrency: CurrencyModel
  quoteState: AsyncStateN.AsyncStateN<
    Error.UnknownError | ApiError.ApiError | NotEnoughLiquidityError,
    Quote.Quote
  >
  selectedPool: PairModel | undefined
  settings: TradeSettings.TradeSettings
  onPairClick: () => void
  onOpenSettings?: () => void
} & ClassNameProps

export const WalletSwapDetailsTable: React.FC<WalletSwapDetailsTableProps> = ({
  quoteState,
  baseCurrency,
  quoteCurrency,
  selectedPool,
  onPairClick,
  onOpenSettings,
  settings,
}) => {
  const { t } = useTranslation()
  const {
    averageWaitTimeState,
    minimumReceiveState,
    networkCostState,
    priceImpactState,
  } = useSwapParameters({
    quoteState,
    baseCurrency,
    quoteCurrency,
  })

  const renderAsyncValue = <T,>(
    as: AsyncStateN.AsyncStateN<unknown, T>,
    skeletonWidth: number,
  ) => {
    if (AsyncStateN.isError(as)) {
      return <>{t('common.error')}</>
    }

    if (AsyncStateN.isSuccess(as)) {
      return as.value
    }

    return (
      <>
        <Skeleton width={skeletonWidth} height={12} />
      </>
    )
  }

  const renderAverageWaitTime = () => {
    return renderAsyncValue(averageWaitTimeState, 40)
  }

  const renderNetworkCost = () => {
    return renderAsyncValue(networkCostState, 60)
  }

  const renderPriceImpact = () => {
    return renderAsyncValue(priceImpactState, 35)
  }

  const renderMinimumReceive = () => {
    return renderAsyncValue(minimumReceiveState, 65)
  }

  const renderPool = () => {
    if (!selectedPool) {
      return undefined
    }
    const { provider } = selectedPool
    return (
      <WalletSwapDetailsLine
        label={t('provider')}
        onChevron={onPairClick}
        value={
          <PoolItem>
            <ProviderIcon size={16} provider={provider} />
            <span>{provider.title}</span>
          </PoolItem>
        }
      />
    )
  }

  return (
    <Root>
      <WalletSwapDetailsLine
        label={t('trading.parameters.network_cost')}
        value={renderNetworkCost()}
      />
      <WalletSwapDetailsLine
        label={t('trading.parameters.avg_wait_time')}
        value={renderAverageWaitTime()}
      />
      <WalletSwapDetailsLine
        label={t('trade.slippage')}
        value={`${settings.slippage}%`}
        onChevron={onOpenSettings}
      />
      <WalletSwapDetailsLine
        label={t('trading.parameters.price_impact')}
        value={renderPriceImpact()}
      />
      <WalletSwapDetailsLine
        label={t('trading.parameters.minimum_receive')}
        value={renderMinimumReceive()}
      />
      {renderPool()}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-gap: var(--swap-details-row-gap);
`

const PoolItem = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: auto auto;
  gap: 6px;

  color: var(--text-11);

  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
`
